import type { AuthChangeEvent } from '@supabase/supabase-js';

/**
 * ENT-2928 — canonical classification of Supabase auth events for
 * full-page gates that subscribe to `onAuthStateChange`.
 *
 * The shared Supabase client broadcasts every auth event (sign-in, token
 * refresh, user-updated, cross-tab swaps, the hash parsed from a
 * verification link by `detectSessionInUrl`) to every subscriber. Several
 * subscribers historically responded to ANY token delta by re-arming a
 * blocking "checking"/loading state, which unmounts or blanks the page —
 * the ENT-2872 workspace-remount class, and the ENT-2928 vendor-claim
 * infinite spinner (verification link opened in a browser that already
 * held a different user's session).
 *
 * ENT-2872 fixed exactly one instance inline (`useAalStepUpGate`). This
 * module extracts that mechanism as the single canonical implementation:
 *
 * - `classifyAuthEvent` maps an auth event to a transition kind:
 *   - `baseline` — the INITIAL session (`INITIAL_SESSION`, and the
 *     `getSession()` read a subscriber performs on mount). It is the
 *     session the subscriber's mount probe already measures, so it only
 *     seeds the baseline; it must never re-arm a gate.
 *   - `token-refresh` — `TOKEN_REFRESHED`. Supabase refreshes the existing
 *     session in place: the user is unchanged and the assurance level is
 *     preserved (aal2 stays aal2). Once a baseline exists, the rotated
 *     token is adopted silently — re-arming here is the remount bug.
 *   - `swap` — everything else (`SIGNED_IN`, `USER_UPDATED`,
 *     `SIGNED_OUT`, `MFA_CHALLENGE_VERIFIED`, …). A swap can change the
 *     user or lower assurance (cross-tab sign-in replacing an aal2
 *     session with aal1), so it must withhold and re-probe.
 *
 * - `createAuthSessionTransitionTracker` owns the baseline bookkeeping and
 *   returns, per event, the action the caller must take. Callers keep only
 *   their own side effects (what "probe" means for them, plus any
 *   gate-specific inertness such as an `enabled` flag).
 */
export type AuthSessionTransitionKind = 'baseline' | 'token-refresh' | 'swap';

/**
 * What the subscriber must do with an observed transition:
 * - `ignore` — nothing changed (duplicate baseline, or a token identical to
 *   the current baseline). Do nothing.
 * - `seed` — the baseline was just established. Do not re-arm; a subscriber
 *   whose first probe is keyed on "baseline observed" may release that wait.
 * - `adopt` — a benign in-place token rotation (refresh with a baseline
 *   present). The tracker adopted the token; do NOT re-arm or re-probe.
 * - `probe` — a genuine session swap, or a refresh that raced ahead of the
 *   baseline (no trusted prior token to compare against — fail closed).
 *   Withhold the gated UI and re-probe.
 */
export type AuthSessionTransitionAction = 'ignore' | 'seed' | 'adopt' | 'probe';

export function classifyAuthEvent(event: AuthChangeEvent): AuthSessionTransitionKind {
  if (event === 'INITIAL_SESSION') return 'baseline';
  if (event === 'TOKEN_REFRESHED') return 'token-refresh';
  return 'swap';
}

export interface AuthSessionTransitionTracker {
  /**
   * Record an observed transition and return the action the subscriber must
   * take. `token` is the session's access token (`undefined`/`null` = no
   * session; normalised to `null` internally).
   */
  consider(kind: AuthSessionTransitionKind, token?: string | null): AuthSessionTransitionAction;
  /** Current baseline token; `undefined` = baseline not yet observed, `null` = signed out. */
  readonly token: string | null | undefined;
  readonly hasBaseline: boolean;
}

/**
 * Decision table (the ENT-2872 mechanism, verbatim):
 *
 * | kind          | baseline? | token same? | action                        |
 * | ------------- | --------- | ----------- | ----------------------------- |
 * | baseline      | no        | —           | seed ref → `seed`             |
 * | baseline      | yes       | yes         | `ignore` (duplicate baseline) |
 * | baseline      | yes       | no          | adopt → `probe` (swap raced   |
 * |               |           |             | getSession vs subscribe)      |
 * | non-baseline  | yes       | yes         | `ignore` (no-op event)        |
 * | token-refresh | yes       | no          | adopt silently → `adopt`      |
 * | token-refresh | no        | —           | adopt → `probe` (init race)   |
 * | swap          | any       | no          | adopt → `probe`               |
 */
export function createAuthSessionTransitionTracker(): AuthSessionTransitionTracker {
  // `undefined` = baseline token not yet established; `null` = no session.
  let baselineToken: string | null | undefined = undefined;

  return {
    get token() {
      return baselineToken;
    },
    get hasBaseline() {
      return baselineToken !== undefined;
    },
    consider(kind, token) {
      const next = token ?? null;
      if (kind === 'baseline') {
        if (baselineToken === undefined) {
          baselineToken = next;
          return 'seed';
        }
        // Baseline already seeded. A second baseline observation is normally a
        // duplicate (both consumers read getSession() AND receive
        // INITIAL_SESSION) — ignore it when the token agrees. But if it
        // DISAGREES, the session was swapped between the caller's getSession()
        // read and its subscription registering (e.g. a cross-tab sign-in that
        // replaces an aal2 session with aal1). Ignoring that would strand the
        // stale token and leave the gate armed under the wrong session, so
        // adopt the new token and re-probe (ENT-2928 R4 P1).
        if (next === baselineToken) return 'ignore';
        baselineToken = next;
        return 'probe';
      }
      if (baselineToken !== undefined && next === baselineToken) return 'ignore';
      const hadBaseline = baselineToken !== undefined;
      baselineToken = next;
      if (kind === 'token-refresh' && hadBaseline) return 'adopt';
      return 'probe';
    },
  };
}
